import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiSave, FiAlertCircle, FiCheck } from 'react-icons/fi';
import { getAdminContent, updateContent } from '../../services/api';

function ContentEditor() {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await getAdminContent();
      setContent(response.data);
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (section, data) => {
    setIsSaving(true);
    setSaveStatus(null);

    try {
      await updateContent(section, data);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleHeroChange = (field, value) => {
    setContent(prev => ({
      ...prev,
      hero: { ...prev.hero, [field]: value }
    }));
  };

  const handleProgramChange = (index, field, value) => {
    const newPrograms = [...content.programs];
    newPrograms[index] = { ...newPrograms[index], [field]: value };
    setContent(prev => ({ ...prev, programs: newPrograms }));
  };

  const handleDomainChange = (index, field, value) => {
    const newDomains = [...content.domains];
    newDomains[index] = { ...newDomains[index], [field]: value };
    setContent(prev => ({ ...prev, domains: newDomains }));
  };

  const handleContactChange = (field, value) => {
    setContent(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
  };

  const handleCustomerVisibilityToggle = (index) => {
    const newCustomers = { ...content.customers };
    newCustomers.list[index].visible = !newCustomers.list[index].visible;
    setContent(prev => ({ ...prev, customers: newCustomers }));
  };

  const handleAtlasLinkChange = (value) => {
    setContent(prev => ({ ...prev, atlas_link: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  const sections = [
    { id: 'hero', label: 'Hero Section' },
    { id: 'programs', label: 'Programs' },
    { id: 'domains', label: 'Engineering Domains' },
    { id: 'customers', label: 'Customers' },
    { id: 'contact', label: 'Contact Info' },
    { id: 'atlas', label: 'Atlas Software Link' },
  ];

  return (
    <div data-testid="content-editor">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-dark-900">Content Editor</h1>
        
        {saveStatus && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center px-4 py-2 rounded-lg ${
              saveStatus === 'success'
                ? 'bg-green-50 text-green-800'
                : 'bg-red-50 text-red-800'
            }`}
          >
            {saveStatus === 'success' ? (
              <>
                <FiCheck className="mr-2" />
                Saved successfully
              </>
            ) : (
              <>
                <FiAlertCircle className="mr-2" />
                Save failed
              </>
            )}
          </motion.div>
        )}
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Section Nav */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm p-4 space-y-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full text-left px-4 py-2 rounded transition-colors ${
                  activeSection === section.id
                    ? 'bg-primary-600 text-white'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
                data-testid={`section-nav-${section.id}`}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>

        {/* Editor Content */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow-sm p-6">
            {/* Hero Section */}
            {activeSection === 'hero' && content?.hero && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-dark-900 mb-4">Hero Section</h2>
                
                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Headline
                  </label>
                  <input
                    type="text"
                    value={content.hero.headline}
                    onChange={(e) => handleHeroChange('headline', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    data-testid="hero-headline-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Subheadline
                  </label>
                  <textarea
                    value={content.hero.subheadline}
                    onChange={(e) => handleHeroChange('subheadline', e.target.value)}
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    data-testid="hero-subheadline-input"
                  />
                </div>

                <button
                  onClick={() => handleSave('hero', content.hero)}
                  disabled={isSaving}
                  className="flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400"
                  data-testid="save-hero-btn"
                >
                  <FiSave className="mr-2" />
                  {isSaving ? 'Saving...' : 'Save Hero'}
                </button>
              </div>
            )}

            {/* Programs Section */}
            {activeSection === 'programs' && content?.programs && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-dark-900 mb-4">Programs</h2>
                
                {content.programs.map((program, index) => (
                  <div key={index} className="p-6 border border-gray-200 rounded-lg space-y-4">
                    <h3 className="font-semibold text-lg">Program {index + 1}</h3>
                    
                    <div>
                      <label className="block text-sm font-semibold text-dark-900 mb-2">
                        Title
                      </label>
                      <input
                        type="text"
                        value={program.title}
                        onChange={(e) => handleProgramChange(index, 'title', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-dark-900 mb-2">
                        Description
                      </label>
                      <textarea
                        value={program.description}
                        onChange={(e) => handleProgramChange(index, 'description', e.target.value)}
                        rows="3"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => handleSave('programs', content.programs)}
                  disabled={isSaving}
                  className="flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400"
                  data-testid="save-programs-btn"
                >
                  <FiSave className="mr-2" />
                  {isSaving ? 'Saving...' : 'Save Programs'}
                </button>
              </div>
            )}

            {/* Domains Section */}
            {activeSection === 'domains' && content?.domains && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-dark-900 mb-4">Engineering Domains</h2>
                
                {content.domains.map((domain, index) => (
                  <div key={index} className="p-6 border border-gray-200 rounded-lg space-y-4">
                    <h3 className="font-semibold text-lg">Domain {index + 1}</h3>
                    
                    <div>
                      <label className="block text-sm font-semibold text-dark-900 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        value={domain.name}
                        onChange={(e) => handleDomainChange(index, 'name', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => handleSave('domains', content.domains)}
                  disabled={isSaving}
                  className="flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400"
                  data-testid="save-domains-btn"
                >
                  <FiSave className="mr-2" />
                  {isSaving ? 'Saving...' : 'Save Domains'}
                </button>
              </div>
            )}

            {/* Customers Section */}
            {activeSection === 'customers' && content?.customers && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-dark-900 mb-4">Customers</h2>
                
                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={content.customers.visible}
                      onChange={(e) => {
                        const newCustomers = { ...content.customers, visible: e.target.checked };
                        setContent(prev => ({ ...prev, customers: newCustomers }));
                      }}
                      className="mr-3 h-4 w-4"
                    />
                    <span className="font-semibold">Show Customers Section on Website</span>
                  </label>
                </div>

                {content.customers.list.map((customer, index) => (
                  <div key={index} className="p-6 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-lg">Customer {index + 1}</h3>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={customer.visible}
                          onChange={() => handleCustomerVisibilityToggle(index)}
                          className="mr-2 h-4 w-4"
                        />
                        <span className="text-sm">Visible</span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-dark-900 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        value={customer.name}
                        onChange={(e) => {
                          const newCustomers = { ...content.customers };
                          newCustomers.list[index].name = e.target.value;
                          setContent(prev => ({ ...prev, customers: newCustomers }));
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => handleSave('customers', content.customers)}
                  disabled={isSaving}
                  className="flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400"
                  data-testid="save-customers-btn"
                >
                  <FiSave className="mr-2" />
                  {isSaving ? 'Saving...' : 'Save Customers'}
                </button>
              </div>
            )}

            {/* Contact Section */}
            {activeSection === 'contact' && content?.contact && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-dark-900 mb-4">Contact Information & Location</h2>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-dark-900 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={content.contact.email}
                      onChange={(e) => handleContactChange('email', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-dark-900 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={content.contact.phone || ''}
                      onChange={(e) => handleContactChange('phone', e.target.value)}
                      placeholder="+91-XXXX-XXXXXX"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                {/* Phone Visibility Toggle */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={content.contact.phone_visible || false}
                      onChange={(e) => handleContactChange('phone_visible', e.target.checked)}
                      className="mr-3 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <span className="font-semibold text-dark-900">Show Phone Number on Website</span>
                      <p className="text-sm text-gray-500">
                        When enabled, the phone number will appear in the Contact section and Footer. 
                        Make sure to enter a valid phone number above before enabling.
                      </p>
                    </div>
                  </label>
                </div>

                <div className="border-t border-gray-200 pt-6 mt-6">
                  <h3 className="text-lg font-bold text-dark-900 mb-4">Factory Address</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-dark-900 mb-2">
                        Address Line 1
                      </label>
                      <input
                        type="text"
                        value={content.contact.address_line1 || ''}
                        onChange={(e) => handleContactChange('address_line1', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        placeholder="Plot No. 8B, Hardware Park, Raviryal"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-dark-900 mb-2">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        value={content.contact.address_line2 || ''}
                        onChange={(e) => handleContactChange('address_line2', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        placeholder="Srisailam Highway, Beside TSIIC Zonal Office"
                      />
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-dark-900 mb-2">
                          City
                        </label>
                        <input
                          type="text"
                          value={content.contact.city || ''}
                          onChange={(e) => handleContactChange('city', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-dark-900 mb-2">
                          State
                        </label>
                        <input
                          type="text"
                          value={content.contact.state || ''}
                          onChange={(e) => handleContactChange('state', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-dark-900 mb-2">
                          Pincode
                        </label>
                        <input
                          type="text"
                          value={content.contact.pincode || ''}
                          onChange={(e) => handleContactChange('pincode', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-dark-900 mb-2">
                          Latitude
                        </label>
                        <input
                          type="number"
                          step="0.000001"
                          value={content.contact.coordinates?.lat || ''}
                          onChange={(e) => {
                            const newContact = { ...content.contact };
                            if (!newContact.coordinates) newContact.coordinates = {};
                            newContact.coordinates.lat = parseFloat(e.target.value);
                            setContent(prev => ({ ...prev, contact: newContact }));
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="17.252972"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-dark-900 mb-2">
                          Longitude
                        </label>
                        <input
                          type="number"
                          step="0.000001"
                          value={content.contact.coordinates?.lng || ''}
                          onChange={(e) => {
                            const newContact = { ...content.contact };
                            if (!newContact.coordinates) newContact.coordinates = {};
                            newContact.coordinates.lng = parseFloat(e.target.value);
                            setContent(prev => ({ ...prev, contact: newContact }));
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="78.475194"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6 mt-6">
                  <h3 className="text-lg font-bold text-dark-900 mb-4">Airport & Transportation</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-dark-900 mb-2">
                        Airport Distance
                      </label>
                      <input
                        type="text"
                        value={content.contact.airport_info?.distance || ''}
                        onChange={(e) => {
                          const newContact = { ...content.contact };
                          if (!newContact.airport_info) newContact.airport_info = {};
                          newContact.airport_info.distance = e.target.value;
                          setContent(prev => ({ ...prev, contact: newContact }));
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        placeholder="45 km"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-dark-900 mb-2">
                        Travel Time
                      </label>
                      <input
                        type="text"
                        value={content.contact.airport_info?.travel_time || ''}
                        onChange={(e) => {
                          const newContact = { ...content.contact };
                          if (!newContact.airport_info) newContact.airport_info = {};
                          newContact.airport_info.travel_time = e.target.value;
                          setContent(prev => ({ ...prev, contact: newContact }));
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        placeholder="50 minutes"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-dark-900 mb-2">
                        Pickup Service Note
                      </label>
                      <textarea
                        value={content.contact.pickup_service || ''}
                        onChange={(e) => handleContactChange('pickup_service', e.target.value)}
                        rows="2"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        placeholder="HADEN can arrange airport pickup upon request"
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleSave('contact', content.contact)}
                  disabled={isSaving}
                  className="flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400"
                  data-testid="save-contact-btn"
                >
                  <FiSave className="mr-2" />
                  {isSaving ? 'Saving...' : 'Save Contact & Location'}
                </button>
              </div>
            )}

            {/* Atlas Software Link Section */}
            {activeSection === 'atlas' && content?.atlas_link !== undefined && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-dark-900 mb-4">Atlas Software Link</h2>
                <p className="text-gray-600 mb-4">
                  This link appears in the top-right navigation as "Login to HADEN Atlas Software"
                </p>
                
                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Atlas Software URL
                  </label>
                  <input
                    type="url"
                    value={content.atlas_link}
                    onChange={(e) => handleAtlasLinkChange(e.target.value)}
                    placeholder="https://your-atlas-software.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Current link: <a href={content.atlas_link} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">{content.atlas_link}</a>
                  </p>
                </div>

                <button
                  onClick={() => handleSave('atlas_link', content.atlas_link)}
                  disabled={isSaving}
                  className="flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400"
                  data-testid="save-atlas-btn"
                >
                  <FiSave className="mr-2" />
                  {isSaving ? 'Saving...' : 'Save Atlas Link'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContentEditor;